import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { EventManager } from '@app/shared/utils/event-manager';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { ProgressBarComponent } from '@app/shared/components/progress-bar/progress-bar.component';
import { SharedModule } from '@app/shared/shared.module';
import { ChallengesStatuses, LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { AssetsService } from '@app/shared/services/assets.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { LimitedTimeEventService } from '@app/shared/services/limitedTimeEvent.service';
import { AppService } from '@app/app.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Streak } from '@app/shared/models/user.model';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { WalletRepository } from '@app/shared/state/wallet/wallet.repository';
import { Throttle } from '@app/shared/decorators/throttle.decorator';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { finalize, Observable, takeUntil } from 'rxjs';
import { ThemingService } from '@app/shared/services/theming.service';

@Component({
  selector: 'slotcom-challenge-card',
  standalone: true,
  imports: [ProgressBarComponent, ButtonComponent, SharedModule],
  templateUrl: './challenge-card.component.html',
  styleUrls: ['./challenge-card.component.scss'],
  animations: [
    trigger('challengeRedeem', [
      state(
        'initial',
        style({
          opacity: 1,
          transform: 'scale(1)',
        })
      ),
      state(
        'exiting',
        style({
          opacity: 0,
          transform: 'scale(0.1)',
          width: 0,
        })
      ),
      transition('initial => exiting', animate('300ms ease-out')),
    ]),
  ],
})
@TakeUntilDestroy
export class ChallengeCardComponent implements OnInit {
  @Input() challenge: ChallengesStatuses;
  @Input() blockedChallenges: number;

  @Output() prestigeRedeemed = new EventEmitter<HTMLElement>();

  @ViewChildren('rewardEl') rewardEls!: QueryList<ElementRef<HTMLElement>>;

  rewards: { value: number; icon: string; key: string }[];
  gameIcon: string = '';
  challengeText: string = '';
  challengeRedeem = false;
  state = signal<'initial' | 'exiting'>('initial');

  loadingRedeemChallenge = signal(false);

  dailyChallenge: LimitedTimeEvent;

  private componentDestroy: () => Observable<unknown>;

  get streak(): Streak {
    return this.userRepo.getStreak();
  }

  private readonly themingService = inject(ThemingService);
  private readonly eventManager = inject(EventManager);
  private readonly walletRepo = inject(WalletRepository);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly assetsService = inject(AssetsService);
  private readonly logger = inject(LoggerService);
  private readonly limitedTimeEventService = inject(LimitedTimeEventService);
  private readonly appService = inject(AppService);
  private readonly userRepo = inject(UserRepository);

  ngOnInit() {
    this.manageChallengeText();
    this.rewardToArray();

    if (!this.challenge.isGeneric) {
      this.gameIcon = `url(${this.assetsService.getGameIconUrl(this.challenge.gameId)})`;
    }

    this.eventsRepo.dailyChallenge$.pipe(takeUntil(this.componentDestroy())).subscribe((event: LimitedTimeEvent) => {
      this.dailyChallenge = event;
    });
  }

  completeChallengeWithGems(gems: number): void {
    if (this.walletRepo.getGems() < gems) {
      this.eventManager.openModal(
        new Modal(NonPersistedModalName.NotEnoughModalComponent, ModalType.View, this.dailyChallenge)
      );
    } else {
      this.eventManager.openModal(
        new Modal(NonPersistedModalName.CompleteWithGemsComponent, ModalType.View, {
          dailyChallenge: this.dailyChallenge,
          challenge: this.challenge,
        })
      );
    }
  }

  redeemChallenge = async (): Promise<void> => {
    this.loadingRedeemChallenge.set(true);
    this.challengeRedeem = true;

    this.limitedTimeEventService
      .redeemGameChallenge(this.dailyChallenge.eventId, this.challenge.id)
      .pipe(finalize(() => this.loadingRedeemChallenge.set(false)))
      .subscribe({
        next: (event: Partial<LimitedTimeEvent>) => {
          const prestigeEl = this.rewardEls
            ?.toArray()
            .map((r) => r.nativeElement)
            .find((el) => el.getAttribute('data-reward-key') === 'prestigeReward');

          if (prestigeEl) {
            this.prestigeRedeemed.emit(prestigeEl);
          }

          if (event) {
            this.state.set('exiting');

            setTimeout(() => {
              this.limitedTimeEventService.updateLimitedTimeEventById(event);
            }, 300);
          }
        },
        error: (err) => {
          this.challengeRedeem = false;
          this.logger.error(err);
        },
      });
  };

  @Throttle(2000)
  goToGame(gameId: string): void {
    if (gameId) {
      this.appService.showGame(gameId);
    }
  }

  private rewardToArray(): void {
    this.rewards = Object.entries(this.buildRawRewards())
      .filter(([value]) => value !== undefined)
      .map(([key, value]) => ({
        value: value,
        icon: this.getRewardIcon(key),
        key: key,
      }));
  }

  private buildRawRewards(): { [key: string]: number } {
    return {
      ...this.challenge.reward,
      pointsReward: this.challenge.pointsReward,
      prestigeReward: this.challenge.prestigeReward,
    };
  }

  private getRewardIcon(rewardItem: string): string {
    switch (rewardItem) {
      case 'credits':
        return `url(${this.themingService.getThemeAsset('none', 'credits')})`;
      case 'gems':
        return `url(${this.themingService.getThemeAsset('none', 'gems')})`;
      case 'prestigeReward':
        return `url(${this.themingService.getThemeAsset('progressline', 'prestige-logo')})`;
      case 'pointsReward':
        return `url(${this.themingService.getThemeAsset('dailychallenge', 'points-icon')})`;
      default:
        return `url(${this.themingService.getThemeAsset('none', 'credits')})`;
    }
  }

  private manageChallengeText(): void {
    this.challengeText = this.challenge
      ? this.limitedTimeEventService.getGameChallengeTransaltionKey(this.challenge)
      : '';
  }
}
